import { updateChartsTimeGap } from 'store/chart/chartSlice';
import { setCurrentDate, setCurrentQuery } from 'store/page/pageSlice';
import {
  getCurrentSlice,
  magicNumbers,
  getFirstQueryIndex,
  isAppLang,
  platformToReducerName,
} from 'utils';
import { filtersInitialState } from './settingsSlice';

const componentsWithSelectedValue = [
  'defaultLandingPageSelectBox',
  'defaultPlatformSelectBox',
  'defaultSubjectSelectBox',
  'defaultLanguageSelectBox',
  'pageContentCountSlider',
  'contentTypeFilter',
  'resourceCategoryFilter',
  'resourcesFilter',
  'commentsOrRelatedPostComments',
  'aiSummarizerPreview',
  'wantTagContent',
  'visiblePlatformTabs',
  'morePlatformTabs',
];

const componentsWithSelectedItems = ['resourcesFilter'];
const componentsWithMinMax = ['advertFilter'];
const componentsWithTimeDuration = ['defaultDateTimeSelectBox'];

const addCurrentFilterSelectedField = ({
  platformSelectedFilters,
  currentFilter,
  filterName,
}) => {
  platformSelectedFilters[filterName] = {};
  if (componentsWithSelectedValue.includes(filterName)) {
    platformSelectedFilters[filterName].selectedValue =
      currentFilter.selectedValue;
  }
  if (componentsWithSelectedItems.includes(filterName)) {
    platformSelectedFilters[filterName].selectedItems =
      currentFilter.selectedItems;
  }
  if (componentsWithMinMax.includes(filterName)) {
    platformSelectedFilters[filterName].selectedObjectValues = {
      min: currentFilter.selectedObjectValues.min,
      max: currentFilter.selectedObjectValues.max,
    };
  }
  if (componentsWithTimeDuration.includes(filterName)) {
    platformSelectedFilters[filterName] = {
      selectedTimeDuration: currentFilter.selectedTimeDuration,
      timeDuration: currentFilter.timeDuration,
      fromDate: currentFilter.fromDate,
      toDate: currentFilter.toDate,
    };
  }
};

export const createFiltersWithOnlySelectedFields = ({
  currentPlatformFilters,
}) => {
  const platformSelectedFilters = {};
  for (const filterName in currentPlatformFilters)
    addCurrentFilterSelectedField({
      platformSelectedFilters,
      currentFilter: currentPlatformFilters[filterName],
      filterName,
    });
  return platformSelectedFilters;
};

if (!isAppLang('ar')) {
  platformToReducerName.instagramComment =
    'instagramCommentByCommentContentReducer';
  platformToReducerName.instagramCommentByPost =
    'instagramCommentByPostContentReducer';
  platformToReducerName.newspaper = 'newspaperContentReducer';
}

const platformToResourceReducerName = {
  news: 'newsSearchResourceReducer',
  instagram: 'instagramSearchResourceReducer',
  twitter: 'twitterSearchResourceReducer',
  virasty: 'virastySearchResourceReducer',
  telegram: 'telegramSearchResourceReducer',
};

export const applyToContentPlatformFilters = ({
  filters,
  sorts,
  platform,
  dispatch,
}) => {
  const { applyWebsiteSettingsFilters, filtersInitialState } = getCurrentSlice(
    platformToReducerName[platform]
  );
  if (typeof applyWebsiteSettingsFilters === 'function') {
    dispatch(
      applyWebsiteSettingsFilters({
        filters,
        sortBy: sorts,
        filtersInitialState,
      })
    );
  }
};

export const applyToResourcePlatformFilters = ({
  filters,
  sorts,
  platform,
  dispatch,
}) => {
  const { applyWebsiteSettingsFilters, filtersInitialState } = getCurrentSlice(
    platformToResourceReducerName[platform]
  );
  if (typeof applyWebsiteSettingsFilters === 'function') {
    dispatch(
      applyWebsiteSettingsFilters({
        filters,
        sortBy: sorts,
        filtersInitialState,
      })
    );
  }
};

export const createCurrentFilterWithAllFields = ({
  allPlatformsFilters,
  responseFilters,
  componentName,
  platform,
}) => {
  if (componentsWithSelectedValue.includes(componentName)) {
    responseFilters[platform][componentName] = {
      ...allPlatformsFilters[platform][componentName],
      ...responseFilters[platform][componentName],
      selectedValue: responseFilters[platform][componentName]?.selectedValue
        ? responseFilters[platform][componentName]?.selectedValue
        : filtersInitialState[platform][componentName]?.selectedValue,
    };
  }

  if (componentsWithSelectedItems.includes(componentName)) {
    responseFilters[platform][componentName] = {
      ...allPlatformsFilters[platform][componentName],
      ...responseFilters[platform][componentName],
      selectedItems:
        Array.isArray(
          responseFilters[platform][componentName]?.selectedItems
        ) || !responseFilters[platform][componentName]?.selectedItems
          ? {}
          : responseFilters[platform][componentName]?.selectedItems,
    };
  }
  if (componentsWithMinMax.includes(componentName)) {
    responseFilters[platform][componentName] = {
      ...allPlatformsFilters[platform][componentName],
      selectedObjectValues: {
        ...allPlatformsFilters[platform][componentName]?.selectedObjectValues,
        ...responseFilters[platform][componentName]?.selectedObjectValues,
        active:
          !responseFilters[platform][componentName] ||
          !responseFilters[platform][componentName]?.selectedObjectValues
            ? false
            : responseFilters[platform][componentName]?.selectedObjectValues
                ?.max !== 1,
      },
    };
  }
  if (componentsWithTimeDuration.includes(componentName)) {
    responseFilters[platform][componentName] = {
      ...allPlatformsFilters[platform][componentName],
      ...responseFilters[platform][componentName],
      timeDuration: responseFilters[platform][componentName]?.timeDuration,
      fromDate: responseFilters[platform][componentName]?.fromDate,
      toDate: responseFilters[platform][componentName]?.toDate,
    };
  }
};

export const createFiltersWithAllFields = ({
  allPlatformsFilters,
  responseFilters,
  platform,
}) => {
  for (const componentName in allPlatformsFilters[platform]) {
    createCurrentFilterWithAllFields({
      allPlatformsFilters,
      responseFilters,
      componentName,
      platform,
    });
  }
};

export const applyDefaultSubjectToCurrentQuery = ({
  defaultSubjectSelectBox,
  thunkAPI,
}) => {
  let queryId = defaultSubjectSelectBox.selectedValue;

  if (queryId === magicNumbers.SUBJECT_FILTER_DEFAULT_OPTION) {
    const queries = thunkAPI.getState().pageReducer.queries.data;
    const firstIndex = getFirstQueryIndex(queries);
    queryId = queries[firstIndex]?.id;
  }
  thunkAPI.dispatch(
    setCurrentQuery({
      queryId,
    })
  );
};

export const applyDefaultDateTimeToCurrentDate = ({
  defaultDateTimeSelectBox,
  dispatch,
}) => {
  const currentDate = {
    fromDate: defaultDateTimeSelectBox.fromDate,
    toDate: defaultDateTimeSelectBox.toDate,
    timeDuration: defaultDateTimeSelectBox.timeDuration,
  };
  dispatch(setCurrentDate(currentDate));
  dispatch(updateChartsTimeGap({ currentDate }));
};

export const applyWebsiteSettingsToOtherReducers = async ({
  filters,
  sorts,
  thunkAPI,
}) => {
  applyDefaultSubjectToCurrentQuery({
    defaultSubjectSelectBox: filters.public.defaultSubjectSelectBox,
    thunkAPI,
  });
  applyDefaultDateTimeToCurrentDate({
    defaultDateTimeSelectBox: filters.public.defaultDateTimeSelectBox,
    dispatch: thunkAPI.dispatch,
  });
  for (const platform in filters) {
    const filtersPlusLanguage = {
      ...filters[platform],
    };
    if (filters.public.defaultLanguageSelectBox) {
      filtersPlusLanguage.languageFilter =
        filters.public.defaultLanguageSelectBox;
    }
    if (platform === 'instagramComment') {
      applyToContentPlatformFilters({
        filters: filtersPlusLanguage,
        sorts: sorts[platform],
        platform: 'instagramCommentByPost',
        dispatch: thunkAPI.dispatch,
      });
    }
    applyToContentPlatformFilters({
      filters: filtersPlusLanguage,
      sorts: sorts[platform],
      platform,
      dispatch: thunkAPI.dispatch,
    });
    applyToResourcePlatformFilters({
      filters: filtersPlusLanguage,
      sorts: sorts[platform],
      platform,
      dispatch: thunkAPI.dispatch,
    });
  }
};
